<template>
  <section class="hero">
    <Splide
      :options="splideOptions"
      class="hero__carousel"
      aria-label="Destaques"
    >
      <SplideSlide>
        <div class="hero__slide hero__slide--app">
          <div class="hero__visual">
            <div class="phone-stage">
              <div class="phone-mockup">
                <span class="phone-mockup__pulse"></span>
                <div class="phone-mockup__notch"></div>
                <img
                  src="@/assets/logo/logo-juf-financeiro-white.svg"
                  alt="Logo Ju Financeiro"
                  class="phone-mockup__logo"
                />
              </div>

              <img
                src="@/assets/icons/icone-dinheiro-min.png"
                alt=""
                class="floating-icon floating-icon--1"
              />
              <img
                src="@/assets/icons/icone-relatorio-min.png"
                alt=""
                class="floating-icon floating-icon--2"
              />
              <img
                src="@/assets/icons/icone-conta-min.png"
                alt=""
                class="floating-icon floating-icon--3"
              />
            </div>
          </div>

          <div class="hero__text">
            <span class="hero__eyebrow">
              {{ $t('message.appComingSoon.eyebrow') }}
            </span>
            <h1
              class="hero__title"
              v-html="$t('message.appComingSoon.title')"
            />
            <p class="hero__subtitle">
              {{ $t('message.appComingSoon.description') }}
            </p>
          </div>

          <div class="platform-badges">
            <a
              :href="appStoreUrl"
              target="_blank"
              rel="noopener"
              class="platform-badge"
            >
              <i class="bi bi-apple"></i>
              iOS
              <small>{{ $t('message.appComingSoon.soon') }}</small>
            </a>
            <a
              :href="playStoreUrl"
              target="_blank"
              rel="noopener"
              class="platform-badge"
            >
              <i class="bi bi-android2"></i>
              Android
              <small>{{ $t('message.appComingSoon.soon') }}</small>
            </a>
          </div>
        </div>
      </SplideSlide>

      <SplideSlide>
        <div class="hero__slide hero__slide--trial">
          <div class="hero__visual">
            <img
              src="https://ju-financeiro-publi.s3.sa-east-1.amazonaws.com/gifs/banner-top-site.gif"
              alt="Ilustração de múltiplas contas de negócio, representando a funcionalidade de gerenciar vários CNPJs em um só lugar."
              class="hero__visual-image"
              loading="lazy"
            />
          </div>

          <div class="hero__text">
            <h1
              class="hero__title"
              v-html="$t('message.introTitle')"
            />
            <p class="hero__subtitle">{{ $t('message.introSubtitle') }}</p>
          </div>

          <div class="hero__cta-wrapper">
            <a
              :href="registerUrl"
              target="_blank"
              class="btn btn-white text-uppercase"
            >
              {{ $t('message.createTrialAccount') }}
            </a>
          </div>
        </div>
      </SplideSlide>
    </Splide>
  </section>
</template>

<script setup>
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/splide/dist/css/themes/splide-default.min.css'
import { APP_STORE_URL, PLAY_STORE_URL } from '@/constants/appStoreLinks'

const registerUrl = 'https://financeiro.janelaunica.com.br/#/registro'
const appStoreUrl = APP_STORE_URL
const playStoreUrl = PLAY_STORE_URL

const splideOptions = {
  type: 'loop',
  perPage: 1,
  arrows: true,
  pagination: true,
  autoplay: true,
  interval: 6000,
  pauseOnHover: true,
  rewind: true,
}
</script>

<style scoped>
@import url('https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css');

.hero {
  padding-top: 5.5rem;
  background: var(--gradient-primary-diagonal);
  overflow: hidden;
  position: relative;
}

.hero__carousel {
  width: 100%;
}

.hero__slide {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  max-width: 1200px;
  min-height: 640px;
  margin: 0 auto;
  padding: 0.75rem 1.5rem 4rem;
  text-align: center;
}

.hero__visual,
.hero__text {
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* App slide: grid so the badges can sit below the phone on mobile
   and next to the text on desktop, regardless of DOM order */
.hero__slide--app {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: 'text' 'visual' 'badges';
}

.hero__slide--app .hero__visual {
  grid-area: visual;
}

.hero__slide--app .hero__text {
  grid-area: text;
}

.hero__slide--app .platform-badges {
  grid-area: badges;
}

/* Trial slide: same idea, CTA button below the gif on mobile */
.hero__slide--trial {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: 'text' 'visual' 'cta';
}

.hero__slide--trial .hero__visual {
  grid-area: visual;
}

.hero__slide--trial .hero__text {
  grid-area: text;
}

.hero__slide--trial .hero__cta-wrapper {
  grid-area: cta;
}

.hero__visual {
  position: relative;
  min-height: 280px;
  justify-content: center;
}

.hero__visual-image {
  width: 70%;
  max-width: 420px;
  height: auto;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

/* Phone mockup (app slide) */
.phone-stage {
  position: relative;
  width: 260px;
  margin: 0 auto;
}

.phone-mockup {
  position: relative;
  width: 190px;
  height: 380px;
  margin: 0 auto;
  border-radius: var(--radius-lg);
  background: linear-gradient(
    160deg,
    rgba(255, 255, 255, 0.22),
    rgba(255, 255, 255, 0.06)
  );
  border: 2px solid rgba(255, 255, 255, 0.4);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: phone-float 4s ease-in-out infinite;
}

.phone-mockup__notch {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 8px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.35);
}

.phone-mockup__logo {
  width: 55%;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.15));
}

.phone-mockup__pulse {
  position: absolute;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  animation: pulse-ring 2.4s ease-out infinite;
}

.floating-icon {
  position: absolute;
  width: 44px;
  height: 44px;
  object-fit: contain;
  padding: 8px;
  background: var(--white);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  animation: icon-float 3.6s ease-in-out infinite;
}

.floating-icon--1 {
  top: 24px;
  left: -10px;
  animation-delay: 0s;
}

.floating-icon--2 {
  top: 90px;
  right: -14px;
  animation-delay: 0.7s;
}

.floating-icon--3 {
  bottom: 40px;
  left: -18px;
  animation-delay: 1.4s;
}

@keyframes phone-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-14px);
  }
}

@keyframes icon-float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-12px) rotate(4deg);
  }
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.75);
    opacity: 0.55;
  }
  100% {
    transform: scale(1.7);
    opacity: 0;
  }
}

/* Text (shared by both slides) */
.hero__eyebrow {
  display: inline-block;
  padding: 0.35rem 1rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  color: var(--white);
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1.25rem;
}

.hero__title {
  font-weight: 700;
  color: var(--white);
  font-family: var(--font-family-main);
  font-size: clamp(2rem, 5vw, 3.2rem);
  line-height: 1.2;
  margin-bottom: 1rem;
}

.hero__title :deep(span) {
  color: var(--white) !important;
  text-decoration: underline;
  text-decoration-color: rgba(255, 255, 255, 0.5);
}

.hero__subtitle {
  color: var(--white);
  opacity: 0.9;
  font-size: clamp(1rem, 2.5vw, 1.3rem);
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.hero__cta-wrapper {
  display: flex;
  justify-content: center;
}

.platform-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}

.platform-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.25rem;
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--color-text-dark);
  font-weight: 700;
  font-size: 1rem;
}

.platform-badge i {
  font-size: 1.3rem;
}

.platform-badge small {
  font-weight: 600;
  color: var(--color-text-highlight);
  border-left: 1px solid var(--c6);
  padding-left: 0.5rem;
  margin-left: 0.15rem;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
}

/* Splide controls: glass/blur so they read over the gradient regardless of slide */
.hero__carousel :deep(.splide__arrow) {
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  opacity: 1;
}

.hero__carousel :deep(.splide__arrow svg) {
  fill: var(--white);
}

.hero__carousel :deep(.splide__arrow:hover) {
  background: rgba(255, 255, 255, 0.4);
}

.hero__carousel :deep(.splide__pagination__page) {
  background: rgba(255, 255, 255, 0.4);
}

.hero__carousel :deep(.splide__pagination__page.is-active) {
  background: var(--white);
  transform: scale(1.3);
}

@media (max-width: 480px) {
  .phone-stage {
    width: 220px;
  }
  .phone-mockup {
    width: 160px;
    height: 320px;
  }
  .floating-icon {
    width: 34px;
    height: 34px;
  }
  .floating-icon--1 {
    top: 16px;
    left: -4px;
  }
  .floating-icon--2 {
    top: 76px;
    right: -4px;
  }
  .floating-icon--3 {
    bottom: 30px;
    left: -6px;
  }
}

@media (min-width: 992px) {
  .hero {
    padding-top: 6rem;
  }

  .hero__slide {
    flex-direction: row;
    text-align: left;
    gap: 4rem;
    min-height: 560px;
    padding: 3rem 1.5rem;
  }

  .hero__visual {
    justify-content: flex-end;
  }

  .hero__text {
    align-items: flex-start;
  }

  .hero__cta-wrapper {
    justify-content: flex-start;
  }

  .platform-badges {
    justify-content: flex-start;
  }

  .hero__slide--app {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'visual text' 'visual badges';
    align-items: center;
  }

  .hero__slide--trial {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'visual text' 'visual cta';
    align-items: center;
  }
}
</style>
